/**
 * Batched mutations for the group apply: up to `size` mutations per request as
 * GraphQL aliases in one document, each with its own variables, so one refused
 * row shows up as an error whose `path[0]` names the alias while the others
 * still return data.
 *
 *   const results = await runBatches(api, ops, { size: 20, concurrency: 5 });
 *   results.get(op.key) → { data } | { errors }
 *
 * An op: { key, mutation, args, fields? } — `args` are the mutation's own
 * argument names; their GraphQL types come from MUTATION_ARGS so values are
 * always sent as variables, never interpolated into the document.
 *
 * Cost: every batch also asks monday for `complexity { query before after
 * reset_in_x_seconds }`, so each request reports what it cost and what is left
 * of the account's per-minute budget (onComplexity). Aliases save requests and
 * round trips; whether they save complexity is what these numbers show.
 *
 * Backoff: a batch whose errors say the complexity budget or rate limit is
 * exhausted, or whose request failed in transit, is retried up to three times
 * (1 s, 2 s, 4 s, or the retry_in_seconds monday sends); anything else is
 * final for that batch.
 */

/** Argument types per mutation, as the monday schema declares them. */
export const MUTATION_ARGS = Object.freeze({
  create_group: { board_id: 'ID!', group_name: 'String!', group_color: 'String', position_relative_method: 'PositionRelative', relative_to: 'String' },
  create_item: { board_id: 'ID!', group_id: 'String', item_name: 'String!', column_values: 'JSON', create_labels_if_missing: 'Boolean' },
  create_subitem: { parent_item_id: 'ID!', item_name: 'String!', column_values: 'JSON', create_labels_if_missing: 'Boolean' },
  create_update: { item_id: 'ID', body: 'String!', mentions_list: '[UpdateMention]' },
  create_column: { board_id: 'ID!', title: 'String!', column_type: 'ColumnType!' },
});

export const DEFAULT_BATCH_SIZE = 20;
export const DEFAULT_CONCURRENCY = 5;
const RETRY_DELAYS_MS = [1000, 2000, 4000];

/** One document for `ops`: aliases op0, op1, … mapped back to the ops' keys. */
export function buildBatch(ops) {
  const varDefs = [];
  const variables = {};
  const lines = [];
  const aliases = {};
  ops.forEach((op, i) => {
    const argTypes = MUTATION_ARGS[op.mutation];
    if (!argTypes) throw new Error(`Unknown mutation ${op.mutation}`);
    const alias = `op${i}`;
    aliases[alias] = op.key;
    const parts = [];
    for (const [arg, value] of Object.entries(op.args || {})) {
      if (value === undefined) continue;
      const type = argTypes[arg];
      if (!type) throw new Error(`Unknown argument ${arg} for ${op.mutation}`);
      const name = `${alias}_${arg}`;
      varDefs.push(`$${name}: ${type}`);
      variables[name] = value;
      parts.push(`${arg}: $${name}`);
    }
    lines.push(`  ${alias}: ${op.mutation}(${parts.join(', ')}) { ${op.fields || 'id'} }`);
  });
  // what this request cost and what is left of the minute's budget (not an op)
  lines.push('  complexity { query before after reset_in_x_seconds }');
  const query = `mutation${varDefs.length ? ` (${varDefs.join(', ')})` : ''} {\n${lines.join('\n')}\n}`;
  return { query, variables, aliases };
}

/** True for the errors monday sends when a request should simply wait and try again. */
export function isRetryable(errors) {
  return (errors || []).some((e) => {
    const code = e && e.extensions && e.extensions.code;
    const msg = String((e && e.message) || '');
    return (
      /complexity|rate limit|too many requests/i.test(msg) ||
      ['ComplexityException', 'COMPLEXITY_BUDGET_EXHAUSTED', 'RateLimitExceeded', 'RATE_LIMIT_EXCEEDED'].includes(code)
    );
  });
}

function retryDelayMs(errors, attempt) {
  const hinted = (errors || []).map((e) => e && e.extensions && e.extensions.retry_in_seconds).find((s) => Number.isFinite(s) && s > 0);
  if (hinted) return hinted * 1000;
  return RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
}

/** monday's first message for a failed op, for the result list. */
export function reasonOf(result) {
  if (!result) return 'no result';
  if (result.errors && result.errors.length) return String(result.errors[0].message || 'monday refused the request');
  if (!result.data) return 'monday returned no data';
  return '';
}

const defaultSleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Sends one batch, retrying on exhaustion; resolves a Map key → { data } | { errors }. */
async function sendBatch(api, ops, { apiVersion, sleep, onComplexity }) {
  const { query, variables, aliases } = buildBatch(ops);
  let res;
  for (let attempt = 0; ; attempt++) {
    try {
      res = await api.api(query, { variables, apiVersion });
    } catch (e) {
      // transport failure: the whole batch is unknown; retry, then fail every op
      if (attempt < RETRY_DELAYS_MS.length) {
        await sleep(retryDelayMs(null, attempt));
        continue;
      }
      res = { errors: [{ message: (e && e.message) || 'request failed' }] };
    }
    if (res && res.errors && isRetryable(res.errors) && attempt < RETRY_DELAYS_MS.length) {
      await sleep(retryDelayMs(res.errors, attempt));
      continue;
    }
    break;
  }

  const out = new Map();
  const data = (res && res.data) || {};
  if (onComplexity && data.complexity) {
    const c = data.complexity;
    onComplexity({
      ops: ops.length,
      mutation: ops[0] && ops[0].mutation,
      cost: c.query,
      before: c.before,
      after: c.after,
      resetInSeconds: c.reset_in_x_seconds,
    });
  }
  const errors = (res && res.errors) || [];
  // an error without a path (document-level) belongs to every op in the batch
  const general = errors.filter((e) => !e || !Array.isArray(e.path) || !e.path.length);
  for (const [alias, key] of Object.entries(aliases)) {
    const own = errors.filter((e) => e && Array.isArray(e.path) && String(e.path[0]) === alias);
    const value = data[alias];
    if (own.length || general.length) out.set(key, { errors: [...own, ...general], data: value || null });
    else if (value) out.set(key, { data: value });
    else out.set(key, { errors: [{ message: 'monday returned no data' }], data: null });
  }
  return out;
}

/**
 * Runs every op, `size` per request, at most `concurrency` requests in flight.
 * onProgress(done, total) fires after each batch; onComplexity({ ops, mutation,
 * cost, before, after, resetInSeconds }) after each request monday answered.
 * Never rejects on a refused op.
 */
export async function runBatches(
  api,
  ops,
  { size = DEFAULT_BATCH_SIZE, concurrency = DEFAULT_CONCURRENCY, apiVersion, onProgress, onComplexity, sleep = defaultSleep } = {},
) {
  const results = new Map();
  if (!ops.length) return results;
  const chunks = [];
  for (let i = 0; i < ops.length; i += size) chunks.push(ops.slice(i, i + size));
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < chunks.length) {
      const chunk = chunks[next++];
      const r = await sendBatch(api, chunk, { apiVersion, sleep, onComplexity });
      for (const [k, v] of r) results.set(k, v);
      done += chunk.length;
      if (onProgress) onProgress(done, ops.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, chunks.length) }, worker));
  return results;
}
